import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Tooltip from "@mui/material/Tooltip";
import ButtonFilled from "./shared/helpers/ButtonFilled";
import SmartphoneOutlinedIcon from "@mui/icons-material/SmartphoneOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ComputerOutlinedIcon from '@mui/icons-material/ComputerOutlined';
import VibrationOutlinedIcon from '@mui/icons-material/VibrationOutlined';
import { toast } from "react-toastify";
import {
  API_resubscribe_email,
  API_unsubscribe_all_email,
} from "../../../constants";
import { postRightworkAPI } from "./utilities/requests";

function groupRolesByType(roles) {
  const grouped = [];
  const groups = new Map();

  roles.forEach((role) => {
    if (!groups.has(role.role_type)) {
      const group = {
        roleType: role.role_type,
        roles: [],
      };
      groups.set(role.role_type, group);
      grouped.push(group);
    }
    groups.get(role.role_type).roles.push(role);
  });

  return grouped;
}

function NotificationSettingsModal(props) {
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "calc(100vw - 32px)",
    maxWidth: 680,
    maxHeight: "95vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    bgcolor: "background.paper",
    border: "2px solid #E2E8F0",
    borderRadius: "15px",
    boxShadow: 24,
  };

  const [data, setData] = React.useState(props.notificationSettings);
  const [roleFilter, setRoleFilter] = React.useState(
    props.notificationSettings?.role_filter ?? null
  );
  const [roleFilterDirty, setRoleFilterDirty] = React.useState(false);
  const [rolesExpanded, setRolesExpanded] = React.useState(false);
  const [applyToAllStores, setApplyToAllStores] = React.useState(false);
  const [isResubscribing, setIsResubscribing] = React.useState(false);
  const [isUnsubscribing, setIsUnsubscribing] = React.useState(false);
  const [unsubscribeConfirmationOpen, setUnsubscribeConfirmationOpen] =
    React.useState(false);

  React.useEffect(() => {
    setData(props.notificationSettings);
    setRoleFilter(props.notificationSettings?.role_filter ?? null);
    setRoleFilterDirty(false);
    setApplyToAllStores(false);
  }, [props.notificationSettings]);


  function toggleCheckmark(itemKey, type) {
    const newData = { ...data.notification_settings };

    // Toggle the boolean value based on itemKey and type
    newData[itemKey][type] = !newData[itemKey][type];

    // Update the state with the modified data
    setData((prev) => ({ ...prev, notification_settings: newData }));
  }

  function toggleRole(roleId) {
    setRoleFilter((prev) =>
      (prev ?? []).includes(roleId)
        ? (prev ?? []).filter((id) => id !== roleId)
        : [...(prev ?? []), roleId]
    );
    setRoleFilterDirty(true);
  }

  function selectAllRoles() {
    setRoleFilter([]);
    setRoleFilterDirty(true);
  }

  function handleSubmit() {
    props.update_notification_settings(
      data.notification_settings,
      data?.role_filter_supported && roleFilterDirty ? roleFilter : undefined,
      canApplyToAllStores && applyToAllStores
    );
    props.handleClose();
  }

  async function handleResubscribe() {
    setIsResubscribing(true);
    try {
      const response = await postRightworkAPI(API_resubscribe_email, {});
      if (response?.status === 200) {
        setData((previous) => ({
          ...previous,
          is_unsubscribed: false,
        }));
        toast.success("Email notifications resumed", { autoClose: 3000 });
      }
    } catch (error) {
      const message =
        error.response?.data?.system?.message ||
        "We couldn't resume email notifications. Please try again.";
      toast.error(message, { autoClose: 3000 });
    } finally {
      setIsResubscribing(false);
    }
  }

  async function handleUnsubscribe() {
    setIsUnsubscribing(true);
    try {
      const response = await postRightworkAPI(API_unsubscribe_all_email, {});
      if (response?.status === 200) {
        setData((previous) => ({
          ...previous,
          is_unsubscribed: true,
        }));
        setUnsubscribeConfirmationOpen(false);
        toast.success("Email notifications paused", { autoClose: 3000 });
      }
    } catch (error) {
      const message =
        error.response?.data?.system?.message ||
        "We couldn't pause email notifications. Please try again.";
      toast.error(message, { autoClose: 3000 });
    } finally {
      setIsUnsubscribing(false);
    }
  }

  const availableRoles = data?.available_roles || [];
  const groupedRoles = groupRolesByType(availableRoles);
  const canApplyToAllStores = (data?.store_membership_count ?? 0) > 1;
  const showRoleFilter =
    data?.role_filter_supported && availableRoles.length > 1;
  const selectedRoleFilter = roleFilter ?? [];
  const allRolesSelected = roleFilter == null || roleFilter.length === 0;
  return (
    <Modal
      open={props.open}
      onClose={props.handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <div className="w-full px-5 pt-4 pb-1 flex items-center justify-between">
          <h2 className="text-2xl">Notification Settings</h2>
          <button
            type="button"
            className="rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            onClick={props.handleClose}
            aria-label="Close notification settings"
          >
            <CloseIcon />
          </button>
        </div>
        {data ? (
          <div className="w-full flex-1 overflow-y-auto">
            <div className="w-full flex flex-col items-center">
              {data.is_unsubscribed && (
                <div
                  className="mx-5 mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3"
                  role="status"
                  aria-live="polite"
                >
                  <div className="flex items-center gap-3">
                    <EmailOutlinedIcon
                      className="text-amber-700"
                      fontSize="small"
                    />
                    <div className="flex-1 font-semibold text-amber-950">
                      Email notifications are paused
                    </div>
                    <button
                      type="button"
                      className="whitespace-nowrap rounded-md bg-amber-700 px-3 py-1.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                      onClick={handleResubscribe}
                      disabled={isResubscribing}
                    >
                      {isResubscribing ? "Resuming…" : "Resume"}
                    </button>
                  </div>
                </div>
              )}
              <div className="w-full mt-3">
                {showRoleFilter && (
                  <div className="w-full border-b border-slate-200">
                    <button
                      className="w-full px-5 py-3 flex flex-row items-center justify-between"
                      onClick={() => setRolesExpanded((v) => !v)}
                    >
                      <div className="flex-1 text-left">
                        <div className="text-slate-500 uppercase text-sm mb-1">
                          Filter by Role
                        </div>
                        <div className="text-slate-700 text-sm">
                          {allRolesSelected
                            ? "All roles"
                            : roleFilter.length === 1
                            ? "1 role selected"
                            : `${roleFilter.length} roles selected`}
                        </div>
                      </div>
                      <KeyboardArrowDownIcon
                        className="text-slate-500"
                        style={{
                          transition: "transform 150ms",
                          transform: rolesExpanded ? "rotate(180deg)" : "rotate(0deg)",
                        }}
                      />
                    </button>
                    {rolesExpanded && (
                      <div className="px-5 pb-4">
                        <div className="text-slate-700 text-sm mb-3">
                          Only notify me about employees with roles:
                        </div>
                        <div className="flex flex-col gap-2">
                          <button
                            className="flex flex-row items-center"
                            onClick={() => selectAllRoles()}
                          >
                            <span
                              className={`w-5 h-5 rounded flex items-center justify-center ${
                                allRolesSelected
                                  ? "bg-emerald-600"
                                  : "bg-slate-50 border border-slate-300"
                              }`}
                            >
                              {allRolesSelected && (
                                <CheckIcon
                                  style={{ color: "white", fontSize: "16px" }}
                                />
                              )}
                            </span>
                            <span className="ml-2 text-base text-slate-900">
                              All roles
                            </span>
                          </button>
                          {groupedRoles.map((group) => (
                            <div key={group.roleType}>
                              <div className="border-t border-slate-100 my-1" />
                              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                                {group.roleType}
                              </div>
                              <div className="flex flex-col gap-2">
                                {group.roles.map((role) => {
                                  const checked = selectedRoleFilter.includes(role.id);
                                  return (
                                    <button
                                      key={role.id}
                                      className="flex flex-row items-center"
                                      onClick={() => toggleRole(role.id)}
                                    >
                                      <span
                                        className={`w-5 h-5 rounded flex items-center justify-center ${
                                          checked
                                            ? "bg-emerald-600"
                                            : "bg-slate-50 border border-slate-300"
                                        }`}
                                      >
                                        {checked && (
                                          <CheckIcon
                                            style={{ color: "white", fontSize: "16px" }}
                                          />
                                        )}
                                      </span>
                                      <span className="ml-2 text-base text-slate-900">
                                        {role.role_name}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="sticky top-0 z-10 bg-white">
                  <div className="w-full flex flex-row px-5 border-b border-slate-200">
                    <div className="w-1/2 h-full"></div>
                    <div className="w-1/2 h-full">
                      <div className="w-full flex flex-row mt-1 pb-1.5">
                        <div className="w-1/3 items-center text-center">
                          <div className="text-slate-500">
                            <ComputerOutlinedIcon />
                          </div>
                          <div className="text-slate-500 mt-1">Web</div>
                        </div>
                        <div className="w-1/3 items-center text-center">
                          <div className="text-slate-500">
                            <VibrationOutlinedIcon />
                          </div>
                          <div className="text-slate-500 mt-1">Push</div>
                        </div>
                        <div className="w-1/3 items-center text-center">
                          <div
                            className={
                              data.is_unsubscribed
                                ? "text-slate-300"
                                : "text-slate-500"
                            }
                          >
                            <EmailOutlinedIcon />
                          </div>
                          <div
                            className={`mt-1 ${
                              data.is_unsubscribed
                                ? "text-slate-300"
                                : "text-slate-500"
                            }`}
                          >
                            Email
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <div>
                    <>
                      {data?.notification_display?.map(
                        ([item, display], index) => (
                          <div key={index}>
                            {item === "title" ? (
                              <div className="w-full px-5 bg-slate-50 py-2.5 border-b border-slate-200 text-sm font-medium text-slate-500">
                                {display}
                              </div>
                            ) : (
                              <div
                                className="flex flex-row items-center py-2.5 px-5 border-b border-slate-200"
                              >
                                <div className="flex flex-row items-center w-1/2 pr-10 min-h-9">
                                  <span className="text-base text-slate-900">
                                    {display}
                                  </span>
                                </div>
                                <div className="flex flex-row flex-1">
                                  <div className="w-1/3 flex items-center justify-center">
                                    <button
                                      onClick={() =>
                                        toggleCheckmark(item, "drawer")
                                      }
                                      className={`w-7 h-7 rounded-full ${
                                        data.notification_settings[item]?.drawer
                                          ? "bg-emerald-600"
                                          : "bg-slate-50 border border-slate-300"
                                      } flex items-center justify-center`}
                                    >
                                      {data.notification_settings[item]
                                        ?.drawer && (
                                        <CheckIcon
                                          style={{
                                            color: "white",
                                            fontSize: "18px",
                                          }}
                                        />
                                      )}
                                    </button>
                                  </div>
                                  <div className="w-1/3 flex items-center justify-center">
                                    <button
                                      onClick={() =>
                                        toggleCheckmark(
                                          item,
                                          "push_notification"
                                        )
                                      }
                                      className={`w-7 h-7 rounded-full ${
                                        data.notification_settings[item]
                                          ?.push_notification
                                          ? "bg-emerald-600"
                                          : "bg-slate-50 border border-slate-300"
                                      } flex items-center justify-center`}
                                    >
                                      {data.notification_settings[item]
                                        ?.push_notification && (
                                        <CheckIcon
                                          style={{
                                            color: "white",
                                            fontSize: "18px",
                                          }}
                                        />
                                      )}
                                    </button>
                                  </div>
                                  <div className="w-1/3 flex items-center justify-center">
                                    <button
                                      onClick={() =>
                                        toggleCheckmark(item, "email")
                                      }
                                      disabled={data.is_unsubscribed}
                                      aria-label={`${display} email notification${
                                        data.is_unsubscribed
                                          ? " (paused)"
                                          : ""
                                      }`}
                                      title={
                                        data.is_unsubscribed
                                          ? "Resume email notifications to change this setting"
                                          : undefined
                                      }
                                      className={`w-7 h-7 rounded-full ${
                                        data.notification_settings[item]?.email
                                          ? data.is_unsubscribed
                                            ? "bg-slate-300"
                                            : "bg-emerald-600"
                                          : data.is_unsubscribed
                                            ? "bg-slate-100 border border-slate-200"
                                            : "bg-slate-50 border border-slate-300"
                                      } flex items-center justify-center disabled:cursor-not-allowed`}
                                    >
                                      {data.notification_settings[item]
                                        ?.email && (
                                        <CheckIcon
                                          style={{
                                            color: data.is_unsubscribed
                                              ? "#64748b"
                                              : "white",
                                            fontSize: "18px",
                                          }}
                                        />
                                      )}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      )}
                    </>
                  </div>
                </div>
                {!data.is_unsubscribed && (
                  <div className="w-full flex justify-center px-5 py-5">
                    <button
                      type="button"
                      className="text-sm text-slate-500 underline hover:text-red-700"
                      onClick={() => setUnsubscribeConfirmationOpen(true)}
                    >
                      Unsubscribe from all email notifications
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div></div>
        )}
        <div className="w-full px-5 py-3 flex flex-col items-center justify-center border-t border-slate-300 gap-2">
          {canApplyToAllStores && (
            <label className="w-full flex flex-row items-center cursor-pointer">
              <Checkbox
                checked={applyToAllStores}
                onChange={(event) => setApplyToAllStores(event.target.checked)}
                size="small"
                sx={{
                  color: "#64748b",
                  "&.Mui-checked": { color: "#059669" },
                }}
              />
              <span className="text-sm text-slate-700">
                Apply these notification settings to all my stores
              </span>
            </label>
          )}
          <ButtonFilled text="Save Settings" onClick={() => handleSubmit()} />
        </div>
        <Dialog
          open={unsubscribeConfirmationOpen}
          onClose={() => {
            if (!isUnsubscribing) setUnsubscribeConfirmationOpen(false);
          }}
          aria-labelledby="unsubscribe-email-dialog-title"
        >
          <DialogTitle id="unsubscribe-email-dialog-title">
            Pause all email notifications?
          </DialogTitle>
          <DialogContent>
            <div className="text-sm text-slate-700">
              Rightwork will stop sending notification and report emails. Your
              individual email choices will stay saved, and web and push
              notifications will not change. Essential account and security
              emails may still be sent.
            </div>
          </DialogContent>
          <DialogActions>
            <button
              type="button"
              className="rounded-md px-3 py-2 text-sm font-semibold text-slate-700 disabled:opacity-60"
              onClick={() => setUnsubscribeConfirmationOpen(false)}
              disabled={isUnsubscribing}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded-md bg-red-700 px-3 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
              onClick={handleUnsubscribe}
              disabled={isUnsubscribing}
            >
              {isUnsubscribing ? "Unsubscribing…" : "Unsubscribe from emails"}
            </button>
          </DialogActions>
        </Dialog>
      </Box>
    </Modal>
  );
}

export default NotificationSettingsModal;
